import { useState, useEffect, useCallback,useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Row, Container } from 'react-bootstrap';
import {
  fetchEventsByCategory,
  fetchEventsByCategoryWithHours,
  fetchEventsBySport,
  setSelectedMatch,
  setSelectedTournament
} from 'stores/match';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import {
  calculateTimeParameter,
  isToday,
  sportTypeToSportId,
  sportTypetToSportIdString
} from 'toolkit/utils.js';
import GoBack from 'components/general/go_back';
import MatchCard from 'components/general/match_card.js';
import SportDetailItem from 'components/sport/SportDetailItem.js';
import Loading from 'components/general/loading';
import { useAllowedSports } from 'toolkit/useHooks/useAllowedSports';
import { fetchOutrightsByParentTournamentReq } from 'stores/outrights/services';
import TournamentCard from 'components/outrights/partials/TournamentCard';

export default function SportDetailsMatches() {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations');
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  const { sportType } = useParams();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category');
  const leagueId = searchParams.get('league');
  const time = searchParams.get('time');
  const [loading, setLoading] = useState(false);
  const [fetchedEvents, setFetchedEvents] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState('');
  const [selectedMarketType, setSelectedMarketType] = useState(1);
  const [outrightEvents, setOutrightEvents] = useState([]);
  const [outrightsSelected, setOutrightsSelected] = useState(false);
  const hasOutrights = outrightEvents.length > 0;
  const selectedMatch = useSelector((state) => state.match.selectedMatch);
  const selectedTournament = useSelector((state) => state.match.selectedTournament);
  const categoryNames = useSelector((state) => state.match.categoryNames);
  const reduxEvents = useSelector((state) => state.match.events);
  const { isSportAllowed } = useAllowedSports();

  const getData = async () => {
    try {
      setLoading(true);
      if (categoryId === 'all') {
        const { betEvents } = await dispatch(
          fetchEventsBySport({
            sport: sportTypeToSportId(sportType),
            dateFilter: calculateTimeParameter(time)
          })
        ).unwrap();
        setFetchedEvents(
          Object.values(betEvents)
            .map((c) => Object.values(c))
            .flat()
            .flat()
            .filter((e) => e && isSportAllowed(e.sport))
        );
      } else if (time) {
        const { betEvents } = await dispatch(
          fetchEventsByCategory({ category: categoryId, dateFilter: calculateTimeParameter(time) })
        ).unwrap();
        setFetchedEvents(
          Object.values(betEvents).flat().filter((e) => e && isSportAllowed(e.sport))
        );
      } else {
        const { betEvents } = await dispatch(
          fetchEventsByCategoryWithHours({ Category: categoryId, Hours: 0 })
        ).unwrap();
        setFetchedEvents(
          Object.values(betEvents).flat().filter((e) => e && isSportAllowed(e.sport))
        );
      }
    } catch (error) {
      // error
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (!loading && fetchedEvents.length > 0) {
      let tempLeagueArr = [];
      if (fetchedEvents.find((e) => isToday(new Date(e.gameDate)))) {
        tempLeagueArr.push({
          leagueId: 'Today',
          leagueName: t(`today`),
          events: []
        });
      }
      fetchedEvents.forEach((event) => {
        if (isToday(new Date(event.gameDate))) {
          const todayIndex = tempLeagueArr.findIndex((l) => l.leagueId === 'Today');
          tempLeagueArr[todayIndex].events.push(event);
        }
        const { leagueId } = event;
        const leagueIndex = tempLeagueArr.findIndex((l) => l.leagueId === leagueId);
        if (leagueIndex === -1) {
          tempLeagueArr.push({
            leagueId,
            leagueName: t(`tournaments.${leagueId}`) !== `tournaments.${leagueId}` ? t(`tournaments.${leagueId}`) : event.league,
            events: [event]
          });
        } else {
          tempLeagueArr[leagueIndex].events.push(event);
        }
      }, []);
      tempLeagueArr = tempLeagueArr.sort((a, b) => {
        if (a.leagueId === 'Today' || b.leagueId === 'Today') return 1;
        return Number(b.leagueId.split(':')[2]) < Number(a.leagueId.split(':')[2]) ? 1 : -1;
      });
      setLeagues(tempLeagueArr);
      if (selectedTournament && tempLeagueArr.find((l) => l.leagueId === selectedTournament)) {
        setSelectedLeague(selectedTournament);
      } else if (leagueId && tempLeagueArr.find((l) => l.leagueId === leagueId)) {
        setSelectedLeague(leagueId);
      } else if (tempLeagueArr.find((l) => l.leagueId === 'Today')) {
        setSelectedLeague('Today');
      } else {
        setSelectedLeague(tempLeagueArr[0].leagueId);
      }
    }
  }, [fetchedEvents]);

  useEffect(() => {
    let cancelled = false;
    setOutrightEvents([]);
    setOutrightsSelected(false);
    const tournamentIds = leagues
      .map((l) => l.leagueId)
      .filter((id) => id && id !== 'Today');
    if (tournamentIds.length === 0) return undefined;
    (async () => {
      const collected = [];
      for (const id of tournamentIds) {
        try {
          const events = await fetchOutrightsByParentTournamentReq(id);
          if (cancelled) return;
          (events || []).forEach((e) => {
            if (Array.isArray(e?.markets) && e.markets.length > 0) collected.push(e);
          });
        } catch (e) {
          // ignore
        }
      }
      if (!cancelled) setOutrightEvents(collected);
    })();
    return () => {
      cancelled = true;
    };
  }, [leagues]);

  useEffect(() => {
    if (selectedLeague && selectedMatch) {
      const selectedMatchCard = document.querySelector(`a[href*="${selectedMatch}"]`);
      if (selectedMatchCard) {
        selectedMatchCard.scrollIntoView({ block: 'center' });
      }
    }
  }, [selectedLeague, selectedMatch]);

  function getLeagues() {
    if (leagues.length > 0) {
      return leagues.map(({ leagueId, events, leagueName }) => (
        <SportDetailItem
          key={leagueId}
          active={!outrightsSelected && selectedLeague === leagueId}
          onClick={() => {
            setOutrightsSelected(false);
            setSelectedLeague(leagueId);
            dispatch(setSelectedMatch(null));
            dispatch(setSelectedTournament(leagueId));
          }}
          text={leagueName}
          count={leagueId === 'Today' ? undefined : events.length}
          icon={
            <img
              className="icon-color"
              width="25"
              height="25"
              src={`${AWS_BUCKET_URL}/Icons/star${
                selectedLeague === leagueId ? 'fill' : 'empty'
              }.svg`}
            />
          }
        />
      ));
    }
    return null;
  }

  function getBetType() {
    const betTypes = [
      {
        label: t('winner'),
        id: 1
      },
      {
        label: t('overunder'),
        id: 18
      },
      {
        label: t('handicap'),
        id: 14
      },
      {
        label: t('nextGoal'),
        id: 8
      },
      {
        label: t('bs'),
        id: 29
      },
      {
        label: t('doubleChance'),
        id: 10
      }
    ];
    return (
      <div className="tip-types shadow">
        {betTypes.map((betType) => (
          <div className="tip-type" key={betType.id}>
            <button
              className={`btn button btn-sm w-100 border fs-12 ${
                selectedMarketType === betType.id ? 'selected' : ''
              }`}
              onClick={() => setSelectedMarketType(betType.id)}>
              {betType.label}
            </button>
          </div>
        ))}
      </div>
    );
  }

  function getBets() {
    const matchCardList = [];
    const leagueArray = [];
    const favoriteMatchIds = new Set();
    const favoriteMatchesByLeague = {};
    
    const selectedLeagueObj = leagues.find((l) => l.leagueId === selectedLeague);
    if (!selectedLeagueObj) return;
    const { events } = selectedLeagueObj;
    
    if (events.length > 0) {
      // Collect favorites grouped by league
      events
        .filter((a) => a.markets && a.league && a.gameNumber)
        .forEach(function (bet) {
          if (reduxEvents[bet.providerId]?.isFavorite) {
            favoriteMatchIds.add(bet.providerId);
            const leagueKey = bet.league;
            if (!favoriteMatchesByLeague[leagueKey]) {
              favoriteMatchesByLeague[leagueKey] = {
                league: bet.league,
                categoryId: bet.categoryId,
                matches: []
              };
            }
            favoriteMatchesByLeague[leagueKey].matches.push(bet);
          }
        });
      
      // Display favorites grouped by league at the top
      Object.keys(favoriteMatchesByLeague).forEach((leagueKey) => {
        const favoriteGroup = favoriteMatchesByLeague[leagueKey];
        if (favoriteGroup.matches.length > 0) {
          const sortedFavorites = favoriteGroup.matches.sort((a, b) => {
            return new Date(a.gameDate).getTime() - new Date(b.gameDate).getTime();
          });
          
          let code = null;
          if (favoriteGroup.categoryId) {
            code = favoriteGroup.categoryId.split(':')[2];
          }
          
          matchCardList.push(
            <div
              className="league-names text-start pb-1 fw-bold pt-1"
              key={'favorite-league-' + leagueKey}>
              {code && (
                <img
                  width="20"
                  height="13"
                  src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                  alt={code}
                  className="me-2"
                />
              )}
              {favoriteGroup.league}
            </div>
          );
          
          sortedFavorites.forEach(function (bet) {
            const findedMarket = bet.markets.find((market) => market.id === selectedMarketType);
            const lock =
              !findedMarket ||
              !findedMarket.outcomes ||
              findedMarket.outcomes.findIndex((element) => element.odds !== null) === -1;
            matchCardList.push(
              <MatchCard
                key={bet.gameNumber}
                marketType={selectedMarketType}
                providerId={bet.providerId}
                lockIfNoMarket={lock}
              />
            );
          });
        }
      });
      
      // Display regular matches (excluding favorites), grouped by league
      events
        .sort((a, b) => {
          // Sort by date
          return new Date(a.gameDate).getTime() - new Date(b.gameDate).getTime();
        })
        .filter((a) => a.markets && a.league && a.gameNumber && !favoriteMatchIds.has(a.providerId))
        .forEach(function (bet) {
          const findedMarket = bet.markets.find((market) => market.id === selectedMarketType);
          const lock =
            !findedMarket ||
            !findedMarket.outcomes ||
            findedMarket.outcomes.findIndex((element) => element.odds !== null) === -1;
          if (!leagueArray.includes(bet.league)) {
            let code = null;
              if (bet.categoryId) {
                code = bet.categoryId.split(':')[2];
              }
            leagueArray.push(bet.league);
            matchCardList.push(
              <div
                className="league-names text-start pb-1 fw-bold pt-1"
                key={'league-' + bet.league}>
                                  {code && (
                  <img
                    width="20"
                    height="13"
                    src={`${AWS_BUCKET_URL}/ClientFlags/${code}.svg`}
                    alt={code}
                    className="me-2"
                  />
                )}
                {bet.league}
              </div>
            );
          }
          return matchCardList.push(
            <MatchCard
              key={bet.gameNumber}
              marketType={selectedMarketType}
              providerId={bet.providerId}
              lockIfNoMarket={lock}
            />
          );
        });
    }
    return matchCardList;
  }

  function titles() {
    const titleList = [];
    titleList.push(
      <div className="float-start w-100 mb-1" key={sportType}>
        <span>{t('sport')} | {t(`sportType.${sportType}`)}</span>
      </div>
    );
    return titleList;
  }

  function categoryName() {
    if (categoryId === 'all') return t('allcategories');
    const selectedLeagueObj = leagues.find((l) => l.leagueId === selectedLeague);
    if (!selectedLeagueObj) return '';
    return (
      categoryNames[selectedLeagueObj.events[0].categoryId] || selectedLeagueObj.events[0].category
    );
  }

  const lastScrollRef = useRef(lastScrollPosition);

  useEffect(() => {
    lastScrollRef.current = lastScrollPosition;
  }, [lastScrollPosition]);

  const handleScroll = useCallback(() => {
    const container = window;
    const stickyEl = document.querySelector('.tip-types');
    if (!stickyEl) return;
    const scrollTop = container.scrollY;
    const direction = scrollTop < lastScrollRef.current ? 'up' : 'down';
    setLastScrollPosition(scrollTop);

    if (scrollTop > 110) {
      stickyEl.classList.add('fixed');
      if (direction === 'down') {
        if (scrollTop > 200) {
          stickyEl.classList.remove('fixed');
        }
      } else {
        if (scrollTop > 200) {
          stickyEl.classList.add('fixed');
        }
      }
    } else {
      stickyEl.classList.remove('fixed');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  if (loading) return <Loading message={t('search.loadingMatches')} />;

  function getBetTypeTitle() {
    if (selectedMarketType === 29) {
      return (
        <Row>
          <Col xs={4} className="textTitle"></Col>
          <Col xs={4} className="textTitle">
            Yes
          </Col>
          <Col xs={4} className="textTitle" style={{ display: 'flex' }}>
            No
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 8) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 14) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 18) {
      return (
        <Row>
          <Col xs={4} className="textTitle"></Col>
          <Col xs={4} className="textTitle">
            +
          </Col>
          <Col xs={4} className="textTitle">
            -
          </Col>
        </Row>
      );
    }
    if (selectedMarketType === 1 || selectedMarketType === 7) {
      return (
        <Row>
          <Col xs={4} className="textTitle">
            1
          </Col>
          <Col xs={4} className="textTitle">
            X
          </Col>
          <Col xs={4} className="textTitle">
            2
          </Col>
        </Row>
      );
    }

    return (
      <Row>
        <Col xs={4} className="textTitle">
          1X
        </Col>
        <Col xs={4} className="textTitle">
          12
        </Col>
        <Col xs={4} className="textTitle">
          X2
        </Col>
      </Row>
    );
  }

  function checkSportTypes(sport) {
    if (
      sport === '1' ||
      sport === '2' ||
      sport === '4' ||
      sport === '3' ||
      sport === '6' ||
      sport === '12' ||
      sport === '13' ||
      sport === '5' ||
      sport === '11' ||
      sport === '14'
    ) {
      return true;
    }
    return false;
  }

  return (
    <>
      <Container fluid className="g-0 scrollable-y">
        <div className="d-flex align-items-center gap-3" style={{ paddingTop: '4px', paddingBottom: '2px' }}>
          <div style={{ marginRight: 3 }} className="d-flex align-items-center flex-shrink-0">
            <GoBack />
          </div>
          <div className="overflow-auto d-flex flex-nowrap gap-3" style={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
            {hasOutrights && (
              <SportDetailItem
                key="outrights"
                active={outrightsSelected}
                onClick={() => setOutrightsSelected(true)}
                text={t('outrights.label')}
              />
            )}
            {getLeagues()}
          </div>
        </div>
        <div className="titleHistoryBack d-flex align-items-center ps-3">
          <h6 className="mb-0 fw-bold text-start text-uppercase">
            {t('sport')} | {t(`sportType.${sportType}`)} | {categoryName()}
          </h6>
        </div>
        <hr className="mx-n3 mt-1 text-color-white" />
        {!outrightsSelected && sportType === 'Football' && (
          <>
            <Row className="mt-n2">
              <Col className="tip-types-container">{getBetType()}</Col>
            </Row>
            <Row>
              <Col></Col>
              <Col>{getBetTypeTitle(selectedMarketType)}</Col>
            </Row>
          </>
        )}

        {!outrightsSelected && sportType !== 'Football' && checkSportTypes(sportTypetToSportIdString(sportType)) && (
          <>
            {/* <Row>
              <Col className="tip-types-container"></Col>
            </Row> */}
            <Row>
              <Col></Col>
              <Col>
                <Row>
                  <Col xs={2} style={{ fontSize: 16 }}></Col>
                  <Col xs={5} style={{ fontSize: 16, textAlign: 'end' }}>
                    1
                  </Col>
                  <Col xs={5} style={{ fontSize: 16 }}>
                    2
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}

        {!outrightsSelected && sportType !== 'Football' && !checkSportTypes(sportTypetToSportIdString(sportType)) && (
          <>
            {/* <Row>
              <Col className="tip-types-container"></Col>
            </Row> */}
            <Row>
              <Col></Col>
              <Col>
                <Row>
                  <Col xs={4} style={{ fontSize: 16, textAlign: 'end' }}>
                    1
                  </Col>
                  <Col xs={4} style={{ fontSize: 16 }}>
                    X
                  </Col>
                  <Col xs={4} style={{ fontSize: 16, textAlign: 'justify' }}>
                    2
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}
        <div className="">
          {outrightsSelected
            ? outrightEvents.map((event) => (
                <TournamentCard key={event.se?.id} event={event} />
              ))
            : getBets()}
        </div>
      </Container>
    </>
  );
}
